import { useCallback, useRef, useState, type FormEvent } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { getImpersonationHeaders } from "@/providers/GraphqlProvider/headers";
import { getAgentChatUrl } from "./constants";
import McpPlaygroundChatView from "./McpPlaygroundChatView";
import { useMcpPlaygroundAuth } from "./useMcpPlaygroundAuth";
import { useMcpPlaygroundAgentChat } from "./useMcpPlaygroundAgentChat";

export default function McpPlaygroundChat() {
  const { getAccessToken } = useCurrentUser();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const { assumedUser } = useAssumeUser();
  const { status: authStatus } = useMcpPlaygroundAuth();
  const api = getAgentChatUrl();
  const [input, setInput] = useState("");

  const authedFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      const token = await getAccessTokenRef.current();
      const headers = new Headers(init?.headers);
      headers.set("Authorization", `Bearer ${token}`);

      Object.entries(getImpersonationHeaders(assumedUser?.id)).forEach(([key, value]) => {
        headers.set(key, value);
      });

      return fetch(input as RequestInfo, {
        ...init,
        headers
      });
    },
    [assumedUser?.id]
  );

  const { messages, sendMessage, isLoading, error } = useMcpPlaygroundAgentChat({
    api,
    fetch: authedFetch as typeof fetch
  });

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const text = input.trim();
      if (!text || isLoading) {
        return;
      }

      setInput("");
      void sendMessage(text);
    },
    [input, isLoading, sendMessage]
  );

  return (
    <McpPlaygroundChatView
      messages={messages}
      input={input}
      isLoading={isLoading}
      error={error}
      authStatus={authStatus}
      onInputChange={setInput}
      onSubmit={handleSubmit}
    />
  );
}
